import {
  Text,
  Textarea,
  VStack,
  Flex,
  Box,
  Group,
  Fieldset,
  ProgressCircle,
  AbsoluteCenter,
  Heading,
  useDisclosure,
  Badge
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  chakraComponents,
  OptionBase,
  Select as ReactSelect
} from "chakra-react-select";
import { absenceData, AbsenceTypeDetail } from "./AbsenceData";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import DateSelectController from "@/components/fields/DateSelect";
import { Field } from "@/components/ui/field";
import { DialogRoot, DialogContent, DialogHeader, DialogBody, DialogCloseTrigger, DialogFooter } from "@/components/ui/dialog";
import { Absence } from "@/api/Absence";
import { AbsenceType } from "@/api/AbsenceType";
import { absenceService } from "@/service/AbsenceService";
import { formatIso8601, formatPicker } from "@/data/DateTimeUtil";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Switch } from "@/components/ui/switch";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";
import ApprovalRejectDialog from "@/components/ApprovalRejectDialog";
import { Alert } from "@/components/ui/alert";
import ApprovalDecisionDetails from "@/components/ApprovalDecisionDetails";
import { workmodelService } from "@/service/WorkModelService";
import { useI18n } from "@/i18n/I18nContext";

interface AbsenceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entry: Absence;
  canEdit: boolean;
  canApprove: boolean;
  canCancel: boolean;
  onSave: (data: Absence) => void | Promise<void>;
  onDelete: (data: Absence) => void;
  onApprove: (data: Absence) => void | Promise<void>;
  onReject: (data: Absence, reason: string) => void | Promise<void>;
  canReject: boolean;
  employeeName?: string;
  isDeciding?: boolean;
  decisionError?: string | null;
}

interface EntryOption extends OptionBase {
  label: string;
  value: AbsenceType;
  data: AbsenceTypeDetail;
}

const options: EntryOption[] = absenceData.map((e) => ({
  label: e.name,
  value: e.type,
  data: e,
}));

const customItem = (item: EntryOption) => (
  <Flex align="center" justifyContent="flex-start">
    <Box bg={item?.data?.color} borderRadius='50%' mr="1rem">
      <item.data.icon size='1rem' />
    </Box>
    <Text ml="1px">{item?.label}</Text>
    <Text ml="1px" fontSize='.8rem' color="gray.500">{item?.data?.detail}</Text>
  </Flex>
);

const customOption = (props) => (
  <chakraComponents.Option label={props.data.name} {...props}>
    {customItem(props.data)}
  </chakraComponents.Option>
);

const AbsenceDialog = ({ isOpen, onClose, entry, canEdit, canApprove, canCancel, onSave, onDelete, onApprove, onReject, canReject,
  employeeName, isDeciding = false, decisionError }: AbsenceDialogProps) => {
  const { t } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    setError,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<Absence>({
    defaultValues: entry
  });

  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure(); // for delete dialog
  const { open: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
  const saveInFlight = useRef(false);

  const [noContract, setNoContract] = useState(false);
  const halfDay = watch("halfDay");
  const [balanceBefore, setBalanceBefore] = useState<number | null>(null);
  const [balanceError, setBalanceError] = useState(false);
  const startTime = watch("localStartDate");
  const endTime = watch("localEndDate");
  const isRejected = entry.id > 0 && ApprovalStatusUtil.isRejected(entry.status);
  const busy = isSubmitting || isDeciding;

  // when opening the dialog
  useEffect(() => {
    reset(entry);
    setNoContract(false);
  }, [entry, reset, isOpen]);

  useEffect(() => {
    let active = true;
    setBalanceBefore(null);
    setBalanceError(false);
    if (!isOpen || canEdit) return;
    workmodelService.contractSummary(entry.userId)
      .then(summary => { if (active) setBalanceBefore(summary.absenceDays); })
      .catch(() => { if (active) setBalanceError(true); });
    return () => { active = false; };
  }, [isOpen, canEdit, entry.userId, entry.organizationId]);

  useEffect(() => {
    let active = true;
    if (isOpen && canEdit && startTime && endTime) {
      const absence = { ...getValues() };
      // the very first assignement has no user and org id
      if (absence?.userId && absence?.organizationId) {
        absence.localStartDate = formatIso8601(absence.localStartDate);
        absence.localEndDate = formatIso8601(absence.localEndDate);
        absenceService.previewAbsence(absence).then(r => {
          if (!active) return;
          setBalanceBefore(r.balanceBefore);
          setValue('days', r.days);
        }).catch(err => {
          if (!active) return;
          if (err.status === 404) {
            console.error('no contracts found');
            // invalidate the fieldset
            setNoContract(true);
          } else {
            console.error(`failed: ${JSON.stringify(err)}`);
          }
        });
      }
    }
    return () => { active = false; };
  }, [entry, startTime, endTime, halfDay, isOpen, canEdit, getValues, setValue]);

  const onSubmit: SubmitHandler<Absence> = async (data) => {
    if (!canEdit || saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      data.localStartDate = formatIso8601(data.localStartDate);
      data.localEndDate = formatIso8601(data.localEndDate);
      await onSave(data);
      onClose();
    } catch {
      setError("root.save", { message: t("approvals.absenceSaveFailed") });
    } finally {
      saveInFlight.current = false;
    }
  };

  const handleClose = () => { if (!saveInFlight.current && !isDeciding) onClose(); };

  return (
    <DialogRoot open={isOpen} onOpenChange={handleClose} size="md" placement='center' motionPreset='scale'>
      <DialogContent>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader display="flex" flexDirection="column" alignItems="stretch" gap={2}>
            {employeeName && <Text fontWeight="bold">{employeeName}</Text>}
            <Heading textAlign='center' mb={2}>
              {!canEdit ? t("approvals.absenceDetails") : entry.id > -1 ? 'Abwesenheit bearbeiten' : 'Neue Abwesenheit'}
              {entry.id > 0 && (
                <Badge ml='.5rem' colorPalette={ApprovalStatusUtil.color(entry.status)}>{ApprovalStatusUtil.label(entry.status)}</Badge>
              )}
            </Heading>
            <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
              {canReject && <Button type="button" disabled={busy} colorPalette="orange" variant="outline" onClick={onRejectOpen}>{t("approvals.actions.reject")}</Button>}
              {canApprove && <Button type="button" disabled={busy} loading={isDeciding} colorPalette="green"
                onClick={() => onApprove(entry)}>{t("approvals.actions.approve")}</Button>}
            </Group>
            {decisionError && <Alert status="error" role="alert">{decisionError}</Alert>}
            {balanceError && <Alert status="warning" role="alert">{t("approvals.balanceFailed")}</Alert>}
          </DialogHeader>
          <DialogCloseTrigger disabled={busy} />

          <Fieldset.Root disabled={noContract || !canEdit || busy} invalid={noContract}>

            <DialogBody>
              <VStack gap={2}>
                {errors.root?.save && <Alert status="error" role="alert">{errors.root.save.message}</Alert>}
                <ApprovalDecisionDetails entry={entry} />
                <Fieldset.ErrorText>
                  Kein Anstellungsverhältnis eingegangen
                </Fieldset.ErrorText>

                <Fieldset.Content>
                  {/* absence data entries with colors */}
                  <Controller control={control} name='absenceType'
                    render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { invalid } }) => (
                      <Field id="entryId" label='Abwesenheit' invalid={invalid} required errorText={errors.absenceType?.message}>
                        <ReactSelect
                          isSearchable={false}
                          autoFocus={canEdit}
                          isDisabled={!canEdit || busy}
                          required
                          ref={ref}
                          name={name}
                          onBlur={onBlur}
                          placeholder='Eintrag wählen'
                          components={{ Option: customOption }}
                          formatOptionLabel={(option: EntryOption) => customItem(option)}
                          options={options}
                          onChange={val => onChange(val?.value)}
                          value={options.find(c => c.value === value)}
                          defaultValue={options[0]}
                        />
                      </Field>
                    )} />

                  {/* Time inputs in the same row */}
                  <Flex gap={3} w="full">
                    <DateSelectController w='9rem' required label='Startzeit' control={control} name='localStartDate' clearable={false} disabled={!canEdit || busy} />
                    <DateSelectController w='9rem' required control={control} label='Endzeit' name='localEndDate' clearable={false} disabled={!canEdit || busy} />

                    <Box display='flex' gap='5' hideBelow='md'>
                      <Controller control={control} name='days'
                        render={({ field: { value, onChange, name, ref }, fieldState: { invalid } }) => {
                          // balance is 100 => how much in percentage is the value
                          const daysPercent = balanceBefore !== null && balanceBefore > 0 ? 100 * value / balanceBefore : 0;
                          return (
                            <Field label='Tagen'>
                              <ProgressCircle.Root size='md' key='days' value={daysPercent} colorPalette='green'>
                                <ProgressCircle.Circle>
                                  <ProgressCircle.Track />
                                  <ProgressCircle.Range strokeLinecap='round' />
                                </ProgressCircle.Circle>
                                <AbsoluteCenter>
                                  <Text>{value}</Text>
                                </AbsoluteCenter>
                              </ProgressCircle.Root>
                            </Field>
                          )
                        }} />
                      <Field label='Saldo'>
                        <ProgressCircle.Root size='md' key='balance' value={100} colorPalette='green'>
                          <ProgressCircle.Circle css={{ "--thickness": "2px" }}>
                            <ProgressCircle.Track />
                            <ProgressCircle.Range />
                          </ProgressCircle.Circle>
                          <AbsoluteCenter>
                                  <Text>{balanceBefore ?? '—'}</Text>
                          </AbsoluteCenter>
                        </ProgressCircle.Root>
                      </Field>
                    </Box>
                  </Flex>

                  {/* Half day checkbox in a new row */}
                  <Flex gap={3} w="full">
                    <Switch id='halfday' colorPalette={'blue'} disabled={!canEdit || busy} checked={halfDay} onCheckedChange={event => setValue('halfDay', event.checked)}>Halbtag</Switch>
                  </Flex>

                  <Field label='Anmerkungen' invalid={!!errors.comments} errorText={errors.comments?.message}>
                    <Textarea
                      placeholder='Kommentar hinzufügen...'
                      {...register("comments")}
                    />
                  </Field>

                </Fieldset.Content>
              </VStack>
            </DialogBody>

          </Fieldset.Root>
          <DialogFooter display="flex" flexDirection="column" alignItems="stretch" gap={2}>
            <ConfirmDialog
              heading={`[${formatPicker(entry.localStartDate)} - ${formatPicker(entry.localEndDate)}] Löschen`}
              isOpen={isDeleteOpen}
              onClose={onDeleteClose}
              onConfirm={async (): Promise<void> => onDelete(entry)}
            />
            <ApprovalRejectDialog
              heading={`[${formatPicker(entry.localStartDate)} - ${formatPicker(entry.localEndDate)}] ablehnen`}
              isOpen={isRejectOpen}
              onClose={onRejectClose}
              onReject={async (reason: string): Promise<void> => onReject(entry, reason)}
            />
            <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
              {canCancel && <Button type="button" disabled={busy} colorPalette="red" me="auto"
                onClick={onDeleteOpen}>Stornieren</Button>}
              <CancelButton disabled={busy} onClick={handleClose} />
              {canEdit && <SubmitButton isSubmitting={isSubmitting} caption={isRejected ? "Erneut einreichen" : undefined} />}
            </Group>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot >
  );
};

export default AbsenceDialog;
